<template>
  <nav ref="mobileHeaderArea" class="navMain mobile flex" :class="{ 'locale-pending': !localeReady }">
    <!-- 메뉴 항목과 드롭다운은 renewal 것을 그대로 쓴다(요구 3). 드로어 껍데기와
         아래 로그인·데모 버튼은 우리 헤더 것이다(요구 1) — 모바일에서 로그인 진입점은
         여기밖에 없어서 renewal 디자인(버튼 없음)을 그대로 따르면 로그인을 못 한다.
         `useRenewalNav` 가 false 면(>1080px) 아무것도 렌더하지 않는다 — 그 폭에서는
         헤더 상단 바가 같은 SiteNav 를 이미 렌더하고 있고, 둘 다 띄우면 id 가 겹친다. -->
    <!-- `.stop` 이 필요하다 — 헤더 `.container` 가 자기 안의 클릭을 받으면 드로어를 닫는다.
         아코디언을 펴려고 누른 클릭까지 닫기로 먹혀 메뉴가 곧바로 사라진다. -->
    <div v-if="useRenewalNav" class="mobileRenewalNav renewal-root" @click.stop="onClickRenewalLink">
      <SiteNav />
    </div>

    <ul class="btnArea flex">
      <li v-if="isAdmin">
        <ul>
          <LazyUserCommonCreditInfo :is-main="true"/>
        </ul>
      </li>
      <!-- Auth-state CTAs gated on `isAuthResolved` to avoid OAuth-callback flash. -->
      <template v-if="userStore.isAuthResolved">
        <li>
          <NuxtLink v-if="!isLoginUser" to="/sign" class="btn whiteLineBtn">
            {{ t("button.sign") }}
          </NuxtLink>
          <NuxtLink v-if="isLoginUser" to="/dashboard" class="btn indigoBtn">
            {{ t("common-dashboard") }}
          </NuxtLink>
        </li>
        <li v-if="!isLoginUser">
          <NuxtLink to="/home/request/demo" class="btn blueBtn">
            {{ t("button.demo") }}
          </NuxtLink>
        </li>
      </template>
    </ul>
  </nav>
</template>

<script setup lang="ts">
import { nextTick, ref, useRoute, watch, onMounted } from "#imports";
import { useI18n } from "vue-i18n";
import type {PropType} from "vue";
import type {HomeMenuType} from "~~/package/config/HomeSubMenuConfig";
import { useUserStore } from "~~/application/store/user/userStore";
import SiteNav from "~~/codepresso-renewal/components/SiteNav.vue";
import { useRenewalLinkInterception } from "~/composables/useRenewalLinkInterception";
// GNB 전용 스코프 CSS. 모든 셀렉터가 `.renewal-root` 를 요구한다.
import "~~/codepresso-renewal/embed/css/nav.css";

const {t} = useI18n({useScope: 'local'})
const props = defineProps({
  menuList: { type: Array as PropType<HomeMenuType[]>, default: () => [] },
  /** 이 드로어가 renewal GNB 를 렌더할지. 헤더가 폭을 보고 정한다(HomeHeaderMain). */
  useRenewalNav: { type: Boolean, default: false },
  isLoginUser: { type: Boolean, default: false }
});
const emit = defineEmits(["onClickMenu", "onClickSubMenu", "resetMenuList"]);
const route = useRoute();
const userStore = useUserStore();
// renewal 링크는 평범한 <a href> 다. DOM 을 고치지 않고 클릭만 가로채 SPA 이동으로 바꾼다.
const onClickRenewalLink = useRenewalLinkInterception();
const mobileHeaderArea = ref(null);

// Hide until after nuxt-i18n applies cookie locale on the client. Without this,
// the menu items / button labels render in the prerender default (en) for a
// frame and swap to the user's actual locale (ko/ja/...) — visible flash.
const localeReady = ref(false);
onMounted(() => { nextTick(() => { localeReady.value = true; }); });

const isAdmin = computed(() =>
  userStore.isAdminType || userStore.isSuperAdminType
)

const onClickMenu = (menuTitle: string) => {
  emit("onClickMenu", menuTitle);
};

const onClickSubMenu = (subMenuTitle: string) => {
  emit("onClickSubMenu", subMenuTitle);
};

const resetMenuList = () => {
  emit("resetMenuList");
};


watch(route, () => {
  if (route.path === "/") {
    resetMenuList();
  } else {
    const strSplit = route.path.split("/");
    onClickSubMenu(strSplit[2] === undefined ? "undefined" : strSplit[2]);
  }
}, { immediate: true });
</script>

<style lang="scss">
@import "~/assets/scss/abstracts/variables.scss";
@import "~/assets/scss/abstracts/mixin.scss";

// ─── 드로어 안의 renewal GNB ────────────────────────────────────────────────
// 메뉴 항목·아코디언 생김새는 renewal 의 embed CSS 가 그대로 그린다(요구 3).
// 여기서 맞추는 것은 **자리**뿐이다 — renewal 의 1080px 이하 규칙은 자기 헤더에 붙는
// 전체화면 패널(`position: absolute; inset: 100% 0 auto; height: 100dvh - 헤더`)을
// 전제하는데, 우리는 이미 드로어 안에 있으므로 그대로 두면 드로어 밖으로 튀어나간다.
.navMain.mobile .mobileRenewalNav {
  width: 100%;

  .nav {
    position: static;
    inset: auto;
    display: block;
    height: auto;
    overflow: visible;
    background: transparent;
    border-top: 0;
  }
}
</style>

<i18n>
{
  "en": {
    "menu": {
      "platform": "Platform",
      "skillCertify": "SkillCertify",
      "skillMonitor": "SkillMonitor",
      "skillAdvance": "SkillAdvance",
      "talentConnect": "TalentConnect",
      "solutions": "Solutions",
      "hiring": "Hiring",
      "resources": "Insights",
      "axlog": "AX.log",
      "news": "News",
      "blog": "Blog",
      "events": "Events",
      "cp": "Customers & Partners",
      "pricing": "Pricing"
    },
    "button": {
      "sign": "Log In",
      "demo": "Request a Demo"
    }
  },
  "ko": {
    "menu": {
      "platform": "기능 소개",
      "solutions": "솔루션",
      "hiring": "채용 평가",
      "resources": "인사이트",
      "axlog": "AX.로그",
      "news": "뉴스",
      "blog": "블로그",
      "events": "이벤트",
      "cp": "고객사 & 파트너사",
      "pricing": "가격"
    },
    "button": {
      "sign": "로그인",
      "dashboard": "대시보드",
      "demo": "무료 체험 신청"
    }
  },
  "ja": {
    "menu": {
      "platform": "プラットフォーム",
      "solutions": "ソリューション",
      "companies": "企業向け",
      "public": "公共分野",
      "education": "教育分野",
      "resources": "インサイト",
      "axlog": "AX.log",
      "news": "ニュース",
      "blog": "ブログ",
      "events": "イベント",
      "cp": "顧客&パートナー",
      "pricing": "価格"
    },
    "button": {
      "sign": "ログイン",
      "dashboard": "ダッシュボード",
      "demo": "デモをリクエスト"
    }
  }
}
</i18n>

<style scoped>
.navMain.mobile.locale-pending { visibility: hidden; }
</style>